import {
  attribute,
  cameraViewMatrix,
  clamp,
  cos,
  cross,
  dot,
  Fn,
  float,
  floor,
  fract,
  instancedArray,
  instanceIndex,
  ivec2,
  length,
  max,
  mix,
  mx_noise_float,
  normalize,
  sin,
  smoothstep,
  textureLoad,
  uint,
  uniform,
  varyingProperty,
  vec2,
  vec3,
  vec4,
} from 'three/tsl';
import * as THREE from 'three/webgpu';
import { mulberry32 } from '../math/rng';
import { mouthClearance, type TerrainMouth } from '../terrain/TerrainMouth';
import { paletteHeight, type TerrainProfile } from '../terrain/TerrainProfile';
import type { TerrainQuadtree } from '../terrain/TerrainQuadtree';
import { surfacePoint } from '../terrain/TerrainShape';
import { TILE_RES, type TileGenerator } from '../terrain/TileGenerator';
import { cellRandoms } from './cellRandom';
import { NearestNodes } from './NearestNodes';
import type { GrassStyle } from './VegetationStyle';

type N = any;
const GRID_C = 32; // clumps per node edge
const CPN = GRID_C * GRID_C;
const BLADES = 9;
const SEGMENTS = 4;
const MAX_NODES = 40;
const RANGE = 70; // metres

const _v = new THREE.Vector3();
const _rel = new THREE.Vector3();

/**
 * GPU grass: clumps of blades generated for the finest terrain nodes near the
 * camera. Each clump's base is computed in the vertex shader with the same
 * cube-sphere and tile math as the terrain, so blades sit exactly on the ground
 * the player sees (no CPU placement, no height queries).
 */
export class GrassRenderer {
  readonly mesh: THREE.Mesh;
  private readonly nodes: N;
  private readonly geometry: THREE.InstancedBufferGeometry;
  private readonly time = uniform(0);
  private readonly cameraLocal = uniform(new THREE.Vector3());
  private readonly pick = new NearestNodes(MAX_NODES);
  /** Grass fades out by this distance: the range, or nearer when the nearest nodes fill the buffer. */
  private readonly fadeEnd = uniform(RANGE);
  count = 0;

  constructor(
    private readonly gen: TileGenerator,
    private readonly tree: TerrainQuadtree,
    private readonly hollowCenter: THREE.Vector3,
    private readonly anchor: THREE.Vector3,
    profile: TerrainProfile,
    style: GrassStyle,
    /** Cave mouths to keep clear of. */
    private readonly mouths: TerrainMouth[] = [],
  ) {
    this.nodes = instancedArray(MAX_NODES * 3, 'vec4');
    this.geometry = this.buildClump();
    this.mesh = new THREE.Mesh(this.geometry, this.buildMaterial(profile, style));
    this.mesh.frustumCulled = false;
    this.mesh.name = 'grass';
  }

  private buildClump(): THREE.InstancedBufferGeometry {
    const rnd = mulberry32(1234);
    const verts: number[] = [];
    const blade: number[] = [];
    const idx: number[] = [];
    for (let b = 0; b < BLADES; b++) {
      const ox = (rnd() - 0.5) * 1.1;
      const oz = (rnd() - 0.5) * 1.1;
      const yaw = rnd() * Math.PI * 2;
      const hs = 0.65 + rnd() * 0.7;
      const base = verts.length / 3;
      for (let k = 0; k <= SEGMENTS; k++) {
        const y = k / SEGMENTS;
        const w = 0.038 * (1 - y * 0.85);
        if (k === SEGMENTS) {
          verts.push(0, y, 0);
          blade.push(ox, oz, yaw, hs);
        } else {
          verts.push(-w, y, 0, w, y, 0);
          blade.push(ox, oz, yaw, hs, ox, oz, yaw, hs);
        }
      }
      for (let k = 0; k < SEGMENTS - 1; k++) {
        const a = base + k * 2;
        idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
      }
      const last = base + (SEGMENTS - 1) * 2;
      idx.push(last, last + 1, last + 2);
    }
    const geo = new THREE.InstancedBufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
    geo.setAttribute('aBlade', new THREE.Float32BufferAttribute(blade, 4));
    geo.setIndex(idx);
    geo.instanceCount = 0;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e9);
    return geo;
  }

  update(cameraUniverse: THREE.Vector3, dt: number): void {
    this.time.value += dt;
    this.cameraLocal.value.subVectors(cameraUniverse, this.anchor);
    _rel.subVectors(this.hollowCenter, this.anchor);
    const arr = this.nodes.value.array as Float32Array;
    // Horizontal distance: project the camera onto the reference surface the node
    // centres live on (the ground itself can be hundreds of metres above it).
    const camLocalHollow = surfacePoint(
      this.tree.shape,
      _v.subVectors(cameraUniverse, this.hollowCenter),
      _v,
    );
    // The finest drawn nodes in range, the nearest first.
    const pick = this.pick;
    pick.reset();
    const cx = camLocalHollow.x;
    const cy = camLocalHollow.y;
    const cz = camLocalHollow.z;
    for (const d of this.tree.drawList) {
      const node = d.node;
      if (node.level !== this.tree.maxLevel) continue;
      // In plain numbers (through Vector3's shared methods every read boxed a number).
      const p = node.point;
      const dx = p.x - cx;
      const dy = p.y - cy;
      const dz = p.z - cz;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) - node.halfDiag;
      if (dist <= RANGE) pick.add(node, dist);
    }
    const count = pick.finish();
    // Fade out before the nodes left out, so turning doesn't pop grass in and out.
    this.fadeEnd.value = Math.min(RANGE, pick.cut);
    let n = 0;
    for (let k = 0; k < count; k++) {
      const node = pick.node(k);
      const size = 2 / 2 ** node.level;
      const sx = this.gen.slotX(node.slot);
      const sy = this.gen.slotY(node.slot);
      const o = n * 12;
      arr[o] = node.point.x + _rel.x;
      arr[o + 1] = node.point.y + _rel.y;
      arr[o + 2] = node.point.z + _rel.z;
      arr[o + 3] = 0;
      arr[o + 4] = -1 + (node.x + 0.5) * size;
      arr[o + 5] = -1 + (node.y + 0.5) * size;
      arr[o + 6] = size;
      arr[o + 7] = node.face;
      arr[o + 8] = sx;
      arr[o + 9] = sy;
      arr[o + 10] = 0;
      arr[o + 11] = 0;
      n++;
    }
    this.count = n;
    this.nodes.value.needsUpdate = true;
    this.geometry.instanceCount = n * CPN;
  }

  private buildMaterial(profile: TerrainProfile, style: GrassStyle): THREE.MeshStandardNodeMaterial {
    const atlas = this.gen.atlas;
    const shape = this.tree.shape.gpu;
    const nodes = this.nodes;
    const time = this.time;
    const cameraLocal = this.cameraLocal;
    const vY = varyingProperty('float', 'vGrassY');
    const vTint = varyingProperty('float', 'vGrassTint');
    const vUp = varyingProperty('vec3', 'vGrassUp');
    const vLush = varyingProperty('vec3', 'vGrassLush');
    const vMeadow = varyingProperty('vec3', 'vGrassMeadow');
    const vTip = varyingProperty('vec3', 'vGrassTip');
    const vBiome = varyingProperty('vec4', 'vGrassBiome');
    const biomeAtlas = this.gen.biomeAtlas;

    const sampleTile = (slot: N, g: N, tex: N = atlas) => {
      const t = g.mul(TILE_RES - 1);
      const t0 = clamp(floor(t), vec2(0), vec2(TILE_RES - 2));
      const f = t.sub(t0);
      const base = slot.add(t0);
      const a = textureLoad(tex, ivec2(base));
      const b = textureLoad(tex, ivec2(base.add(vec2(1, 0))));
      const c = textureLoad(tex, ivec2(base.add(vec2(0, 1))));
      const d = textureLoad(tex, ivec2(base.add(vec2(1, 1))));
      return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
    };

    const position = Fn(() => {
      const idx = instanceIndex;
      const node = idx.div(uint(CPN));
      const clump = idx.sub(node.mul(uint(CPN)));
      const d0 = nodes.element(node.mul(uint(3)));
      const d1 = nodes.element(node.mul(uint(3)).add(uint(1)));
      const d2 = nodes.element(node.mul(uint(3)).add(uint(2)));
      const uc = d1.x;
      const vc = d1.y;
      const size = d1.z;
      const rnd = cellRandoms(clump, CPN, d1);
      const jitter = vec2(rnd(0), rnd(1)).sub(0.5).mul(0.85);
      const gi = vec2(float(clump.mod(uint(GRID_C))), float(clump.div(uint(GRID_C))));
      const g = gi.add(0.5).add(jitter).div(GRID_C);
      const surf = shape.at(d1.w, uc, vc, size, g);
      const dir = surf.normal;
      const tile = sampleTile(d2.xy, g);
      const h = tile.x;
      const slope = length(vec2(tile.z, tile.w));
      const base = d0.xyz.add(surf.offset).sub(dir.mul(max(h, 0)));
      const up = dir.negate();
      const t1 = normalize(surf.U.sub(dir.mul(dot(surf.U, dir))));
      const t2 = cross(up as N, t1 as N);

      // Where grass grows: dry land below the tree line, not on cliffs or beaches.
      const dist = length(base.sub(cameraLocal));
      const minH = style.minHeight ?? 3.5;
      const hp = paletteHeight(profile, h);
      // Colours and growth that change from place to place (a world of several biomes).
      const biome = biomeAtlas ? sampleTile(d2.xy, g, biomeAtlas) : undefined;
      if (biome) vBiome.assign(biome);
      const here = style.local?.({ p: surf.local.sub(dir.mul(max(h, 0))), h: hp, slope, biome });
      if (here) {
        vLush.assign(here.lush);
        vMeadow.assign(here.meadow);
        vTip.assign(here.tip);
      }
      const grow = smoothstep(minH, minH * 1.7, hp)
        .mul(here ? here.grow : float(1))
        .mul(float(1).sub(smoothstep(0.32, 0.5, slope)))
        .mul(float(1).sub(smoothstep(profile.snowLine * 0.45, profile.snowLine * 0.6, hp)))
        .mul(float(1).sub(smoothstep(this.fadeEnd.mul(0.6), this.fadeEnd, dist)))
        .mul(smoothstep(0.08, 0.3, mx_noise_float(base.mul(0.06)).mul(0.5).add(0.62)))
        .mul(mouthClearance(surf.local.sub(dir.mul(max(h, 0))), this.mouths, 0.2, 1.0, 0.8));

      const blade = attribute('aBlade', 'vec4');
      const local = attribute('position', 'vec3');
      const yaw = blade.z;
      const height = blade.w
        .mul(0.5 * (style.height ?? 1))
        .mul(grow)
        .mul(here ? here.height : float(1));
      const lateral = vec2(cos(yaw), sin(yaw)).mul(local.x);
      const y = local.y;
      // Blades arch over, each its own amount, toward the way it faces (tips a little lower).
      const lean = fract(yaw.mul(7.13)).mul(0.3).add(0.08);
      const arch = vec2(sin(yaw).negate(), cos(yaw)).mul(lean.mul(y.mul(y)).mul(height));
      const rise = y.mul(height).mul(float(1).sub(lean.mul(y).mul(y).mul(0.4)));
      // Wind: slow gusts plus a quick flutter, bending more toward the tip.
      const gust = mx_noise_float(base.mul(0.045).add(vec3(time.mul(0.35), 0, time.mul(0.2)))).mul(0.55);
      const flutter = sin(time.mul(2.3).add(rnd(2).mul(40))).mul(0.12);
      const bend = t1
        .mul(gust.add(flutter))
        .add(t2.mul(gust.mul(0.4)))
        .mul(y.mul(y).mul(height));
      vY.assign(y);
      vTint.assign(rnd(3));
      vUp.assign(up);
      return base
        .add(t1.mul(blade.x.add(lateral.x).add(arch.x)))
        .add(t2.mul(blade.y.add(lateral.y).add(arch.y)))
        .add(up.mul(rise))
        .add(bend);
    });

    const mat = new THREE.MeshStandardNodeMaterial({
      side: THREE.DoubleSide,
      roughness: style.roughness,
      metalness: 0,
    });
    mat.positionNode = position();
    const lush = style.local ? vLush : vec3(...style.lush);
    const meadow = style.local ? vMeadow : vec3(...style.meadow);
    const tip = style.local ? vTip : vec3(...style.tip);
    const baseCol = mix(lush, meadow, vTint);
    // Toward the tip: a share of dry blades turn the tip colour, the rest stay green and catch
    // more light (see GrassStyle.dryTips); or every blade leans toward the tip colour.
    const share = style.dryTips;
    const tipCol =
      share === undefined
        ? mix(baseCol, tip, vTint.mul(0.5))
        : mix(baseCol.mul(1.25), tip, smoothstep(1 - share - 0.04, 1 - share + 0.04, vTint));
    const col = mix(baseCol.mul(0.62), tipCol, smoothstep(0, 1, vY));
    mat.colorNode = vec4(col, 1);
    // Soft, mostly-up normals keep a field of blades from sparkling.
    mat.normalNode = normalize(cameraViewMatrix.mul(vec4(normalize(vUp), 0)).xyz);
    let emissive: N = null;
    const biomeHere = biomeAtlas ? vBiome : undefined;
    if (style.glow) emissive = style.glow(biomeHere).mul(smoothstep(0, 1, vY).mul(0.6).add(0.4));
    if (style.tipGlow) {
      // Glowing tips, brightest at the very end of each blade, varying blade to blade.
      const tip = style
        .tipGlow(biomeHere)
        .mul(smoothstep(0.55, 1, vY))
        .mul(vTint.mul(vTint).mul(1.4).add(0.2));
      emissive = emissive ? emissive.add(tip) : tip;
    }
    if (emissive) mat.emissiveNode = emissive;
    return mat;
  }
}
